import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Box, Button, FormControlLabel, Switch, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CommerceDeliveryConfigFormProps from 'types/forms/CommerceDeliveryConfigFormProps';
import CommerceDeliveryConfig from 'types/models/CommerceDeliveryConfig';
import PickUpVerificationMethod from 'types/models/PickUpVerificationMethod';
import CommerceDeliveryConfigForm from './schemas/CommerceDeliveryConfigForm';
import ConfirmDialog from 'components/ConfirmDialog';
import AreasInput from 'components/AreasInput';
import SelectField from 'components/SelectField';
import { pickUpVerificationMethodsForSelect } from 'utils/delivery';
import {
  destroyTimeRangeConfig,
  storeTimeRangeConfig,
  updateCommerceDeliveryConfig,
  updateTimeRangeConfig,
} from 'services/commerceServices';
import { getPickUpVerificationMethods } from 'services/pickupVerificationMethodServices';
import { DeliveryTimeRangeConfig } from 'types/models/VenueDeliveryConfig';
import AddOrRemoveTimeRange from 'components/AddOrRemoveTimeRange';
import InfoBanner from 'components/InfoBanner';

export default ({ commerce }: CommerceDeliveryConfigFormProps): JSX.Element => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [pickUpVerificationMethods, setPickUpVerificationMethods] = React.useState<PickUpVerificationMethod[]>([]);
  const { t } = useTranslation();
  const codeVerificationMethods = pickUpVerificationMethodsForSelect(pickUpVerificationMethods, t);
  const [bannerMessage, setBannerMessage] = React.useState('');
  const [infoBannerResult, setInfoBannerResult] = React.useState<boolean>(false);

  const onConfirm = async (): Promise<void> => {
    if (!commerce) return;

    const dataCommerceDeliveryConfig: CommerceDeliveryConfig = {
      id: '',
      deliveryEnabled: formik.values.deliveryEnabled,
      pickUpEnabled: formik.values.pickUpEnabled,
      isLocalDelivery: formik.values.isLocalDelivery,
      showPhoneField: formik.values.showPhoneField,
      isPhoneFieldRequired: formik.values.isPhoneFieldRequired,
      pickUpVerificationMethod: formik.values.pickUpVerificationMethod,
      areas: formik.values.areas,
      timeRangeConfigs: formik.values.deliveryTimeRangeConfigs,
      purchaseToleranceTime: formik.values.purchaseToleranceTime,
    };

    try {
      const result = await updateCommerceDeliveryConfig(commerce.id, dataCommerceDeliveryConfig);
      if (result) {
        history.go(-1);
      } else {
        throw new Error();
      }
    } catch (error) {
      formik.setSubmitting(false);
    }
  };

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      deliveryEnabled: commerce.deliveryConfig?.deliveryEnabled ?? false,
      pickUpEnabled: commerce.deliveryConfig?.pickUpEnabled ?? false,
      isLocalDelivery: commerce.deliveryConfig?.isLocalDelivery ?? false,
      showPhoneField: commerce?.deliveryConfig?.showPhoneField ?? false,
      isPhoneFieldRequired: commerce?.deliveryConfig?.isPhoneFieldRequired ?? false,
      pickUpVerificationMethod: commerce?.deliveryConfig?.pickUpVerificationMethod ?? null,
      areas: commerce.deliveryConfig?.areas ?? [],
      purchaseToleranceTime: commerce?.deliveryConfig?.purchaseToleranceTime ?? null,
      deliveryTimeRangeConfigs: commerce.deliveryConfig?.timeRangeConfigs ?? [],
    },
    validationSchema: CommerceDeliveryConfigForm(t),
    onSubmit: (): void => {
      setOpenDialog(true);
    },
  });

  const onDeleteTimeRangeConfigRow = async (ageRangeConfig: DeliveryTimeRangeConfig) => {
    if (!commerce) return;
    const timeRangeConfigResult = await destroyTimeRangeConfig(commerce.id, ageRangeConfig.id);
    if (timeRangeConfigResult !== null && commerce.deliveryConfig && commerce.deliveryConfig.timeRangeConfigs) {
      let values = formik.values.deliveryTimeRangeConfigs;
      values = formik.values.deliveryTimeRangeConfigs.filter((config) => config.id !== timeRangeConfigResult.id);
      formik.setFieldValue('deliveryTimeRangeConfigs', [...values]);

      setInfoBannerResult(false);
      setBannerMessage(t('venueDeliveryConfig-form-deleted-range-time'));
    }
  };

  const onAddTimeRangeConfigRow = async (timeRangeConfig: DeliveryTimeRangeConfig): Promise<void> => {
    if (!commerce) return;
    const body = {
      fromHour: timeRangeConfig.fromHour,
      toHour: timeRangeConfig.toHour,
      commerceConfigId: commerce.deliveryConfig?.id,
    };

    const timeRangeConfigResult = await storeTimeRangeConfig(commerce.id, body);
    if (timeRangeConfigResult !== null) {
      const values = formik.values.deliveryTimeRangeConfigs;
      values.push(timeRangeConfigResult);
      formik.setFieldValue('deliveryTimeRangeConfigs', [...values]);

      setInfoBannerResult(true);
      setBannerMessage(t('venueDeliveryConfig-form-created-range-time'));
    }
  };

  const onUpdateTimeRangeConfigRow = async (timeRangeConfig: DeliveryTimeRangeConfig): Promise<void> => {
    if (!commerce) return;
    const body = {
      fromHour: timeRangeConfig.fromHour,
      toHour: timeRangeConfig.toHour,
      time: commerce.deliveryConfig?.id,
    };

    const timeRangeConfigResult = await updateTimeRangeConfig(commerce.id, body, timeRangeConfig.id);
    if (timeRangeConfigResult !== null) {
      const configIndex = commerce.deliveryConfig?.timeRangeConfigs.findIndex(
        (config) => config.id === timeRangeConfigResult.id,
      );
      if (configIndex !== undefined && configIndex !== -1 && commerce.deliveryConfig) {
        commerce.deliveryConfig.timeRangeConfigs[configIndex] = timeRangeConfigResult;
      }

      setInfoBannerResult(true);
      setBannerMessage(t('venueDeliveryConfig-form-updated-range-time'));
    }
  };

  React.useEffect(() => {
    const getAllPickUpVerificationMethods = async () => {
      if (formik.values.pickUpEnabled) {
        const pickUpVerificationMethods = await getPickUpVerificationMethods();
        setPickUpVerificationMethods(pickUpVerificationMethods);
      } else {
        if (pickUpVerificationMethods.length > 0 || formik.values.pickUpVerificationMethod !== null) {
          setPickUpVerificationMethods([]);
          formik.setFieldValue('pickUpVerificationMethod', null);
        }
      }
    };
    getAllPickUpVerificationMethods();
  }, [formik.values.pickUpEnabled]);

  return (
    <>
      <InfoBanner
        message={bannerMessage}
        result={infoBannerResult}
        onClose={() => {
          setInfoBannerResult(false);
          setBannerMessage('');
        }}
      />
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <FormControlLabel
            id="deliveryEnabled"
            name="deliveryEnabled"
            control={<Switch checked={formik.values.deliveryEnabled} onChange={formik.handleChange} />}
            label={t('commerceDeliveryConfig-form-deliveryEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="pickUpEnabled"
            name="pickUpEnabled"
            control={<Switch checked={formik.values.pickUpEnabled} onChange={formik.handleChange} />}
            label={t('commerceDeliveryConfig-form-pickUpEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        {formik.values.deliveryEnabled && (
          <Box>
            <FormControlLabel
              id="isLocalDelivery"
              name="isLocalDelivery"
              control={<Switch checked={formik.values.isLocalDelivery} onChange={formik.handleChange} />}
              label={t('commerceDeliveryConfig-form-isLocalDelivery')}
              disabled={formik.isSubmitting}
            />
          </Box>
        )}
        {formik.values.deliveryEnabled && (
          <Box>
            <FormControlLabel
              id="showPhoneField"
              name="showPhoneField"
              control={<Switch checked={formik.values.showPhoneField} onChange={formik.handleChange} />}
              label={t('commerceDeliveryConfig-form-showPhoneField')}
              disabled={formik.isSubmitting}
            />
            {formik.values.showPhoneField && (
              <FormControlLabel
                id="isPhoneFieldRequired"
                name="isPhoneFieldRequired"
                control={<Switch checked={formik.values.isPhoneFieldRequired} onChange={formik.handleChange} />}
                label={t('commerceDeliveryConfig-form-isPhoneFieldRequired')}
                disabled={formik.isSubmitting}
              />
            )}
          </Box>
        )}
        {formik.values.pickUpEnabled && codeVerificationMethods && (
          <SelectField
            name="pickUpVerificationMethod"
            label={t('commerceDeliveryConfig-form-pickUpVerificationMethodConfig')}
            placeholder={t('commerceDeliveryConfig-form-pickUpVerificationMethodConfig-placeholder')}
            value={formik.values.pickUpVerificationMethod}
            options={codeVerificationMethods}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.pickUpVerificationMethod}
            error={formik.errors.pickUpVerificationMethod}
            isClearable={true}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable={true}
          />
        )}

        {(formik.values.deliveryEnabled || formik.values.pickUpEnabled) && (
          <>
            <TextField
              fullWidth
              margin="dense"
              id="purchaseToleranceTime"
              name="purchaseToleranceTime"
              label={t('venueDeliveryConfig-form-purchase-tolerance-time')}
              value={formik.values.purchaseToleranceTime}
              onChange={formik.handleChange}
              type="number"
            />

            {commerce.deliveryConfig?.timeRangeConfigs && (
              <AddOrRemoveTimeRange
                onDeleteRow={onDeleteTimeRangeConfigRow}
                onAddRow={onAddTimeRangeConfigRow}
                onUpdateRow={onUpdateTimeRangeConfigRow}
                inputName={'deliveryTimeRangeConfigs'}
                values={formik.values.deliveryTimeRangeConfigs}
                setFieldValue={formik.setFieldValue}
              />
            )}
          </>
        )}

        <AreasInput
          type={'Comercio'}
          name={'areas'}
          values={formik.values.areas}
          setFieldValue={formik.setFieldValue}
          isDisabled={formik.isSubmitting}
        />

        {formik.errors.areas && <div className="fields-required-alert">{t('general-suggestions.field-required')}</div>}

        <Box marginTop="1rem">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {t('save-config')}
          </Button>
        </Box>
      </form>
    </>
  );
};
